import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  SvgIcon,
  Tooltip
} from '@mui/material';
import {
  useGridApiRef,
  GridFooterContainer,
  GridPagination
} from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import chunk from 'lodash/chunk';
import { RootState } from '../../redux/store';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import Loader from '../../components/Loader/Loader';
import electivesAssignmentApi from '../../apis/electivesAssignment';
import { buildElectiveColumns } from './utils/columns';
import { UpdateAssignmentRecord } from './types/ElectiveAssignmentTypes';
import {
  buildStudentsMapping,
  getAllElectives,
  isSubjectAssigned,
  updateStudentRecord
} from './utils/utils';
import { updateAssignment } from './utils/updateAssignment';
import SearchFilters from './components/SearchFilters';
import { StyledDataGrid } from './styled';
import { ElectiveSubjectsMapping } from '../../types/electiveAssignmentType';
import fetchTermIdsForClass from './utils/terms';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { PageWrapper, Title } from '../common/styled';
import pallet from '../../layouts/pallet';

declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    onAutosize?: () => void;
    autoFitEnabled?: boolean;
    pinSecondColumn?: boolean;
    onTogglePinSecondColumn?: () => void;
  }
}

const PINNABLE_FIELDS = ['studentName', 'enrollmentNo'];
const PINNED_COLUMN_WIDTH: Record<string, number> = {
  studentName: 180,
  enrollmentNo: 160
};

const CustomFooter = ({
  onAutosize,
  autoFitEnabled,
  pinSecondColumn,
  onTogglePinSecondColumn
}: {
  onAutosize?: () => void;
  autoFitEnabled?: boolean;
  pinSecondColumn?: boolean;
  onTogglePinSecondColumn?: () => void;
}) => (
  <GridFooterContainer>
    <Box display='flex' alignItems='center' gap={1} sx={{ ml: 1 }}>
      <Tooltip
        title={
          autoFitEnabled
            ? 'Auto-fit column widths (on)'
            : 'Auto-fit column widths'
        }
      >
        <IconButton
          size='small'
          onClick={onAutosize}
          sx={{
            color: autoFitEnabled ? pallet.primary : undefined,
            backgroundColor: autoFitEnabled
              ? `${pallet.primary}1F`
              : 'transparent'
          }}
        >
          <SvgIcon fontSize='small'>
            <path d='M9.01 14H2v2h7.01v3L13 15l-3.99-4v3zm5.98-1v-3H22V8h-7.01V5L11 9l3.99 4z' />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <FormControlLabel
        control={
          <Checkbox
            size='small'
            checked={!!pinSecondColumn}
            onChange={onTogglePinSecondColumn}
          />
        }
        label='Pin Enrollment No'
      />
    </Box>
    <GridPagination />
  </GridFooterContainer>
);

const STUDENTS_MAPPING_BATCH_SIZE = 20;

const StudentsElectiveSubjectsPage: React.FC = () => {
  const school = useSelector((state: RootState) => state.profile.user.school);
  const user = useSelector((state: RootState) => state.profile.user);

  const [academicYear, setAcademicYear] = useState('');
  const [students, setStudents] = useState<ElectiveSubjectsMapping[]>([]);
  const [termIds, setTermIds] = useState<string[]>([]);
  const [originalStudents, setOriginalStudents] = useState<
    ElectiveSubjectsMapping[]
  >([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignmentChanges, setAssignmentChanges] =
    useState<UpdateAssignmentRecord>({});
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: SnackbarSeverityType.success
  });
  const [savedGrade, setSavedGrade] = useState('');
  const [savedDivision, setSavedDivision] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const CONFIRM_MESSAGE = `Un-assigning subjects (if any) will delete the marks entered for them and the subject won't appear on the report card. If you want the subject to show, don't unassign it. Are you sure you want to save the changes?`;

  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [gridHeight, setGridHeight] = useState(400);
  const [pinSecondColumn, setPinSecondColumn] = useState(true);

  const pinnedFields = useMemo(
    () => (pinSecondColumn ? PINNABLE_FIELDS : PINNABLE_FIELDS.slice(0, 1)),
    [pinSecondColumn]
  );

  const pinnedColumnWidths = useMemo(
    () =>
      Object.fromEntries(
        pinnedFields.map((field) => [field, PINNED_COLUMN_WIDTH[field]])
      ),
    [pinnedFields]
  );

  const [autoFitColumnWidths, setAutoFitColumnWidths] = useState<
    Record<string, number>
  >({});

  const fixedColumnWidths = useMemo(
    () => ({ ...autoFitColumnWidths, ...pinnedColumnWidths }),
    [autoFitColumnWidths, pinnedColumnWidths]
  );

  const pinnedColumnsSx = useMemo(() => {
    let left = 0;
    const sx: Record<string, object> = {};
    pinnedFields.forEach((field) => {
      sx[`& [data-field="${field}"]`] = {
        position: 'sticky',
        left,
        zIndex: 2,
        backgroundColor: pallet.white
      };
      sx[`& .MuiDataGrid-row:nth-of-type(odd) [data-field="${field}"]`] = {
        backgroundColor: pallet.grayBackground
      };
      left += PINNED_COLUMN_WIDTH[field];
    });
    const lastField = pinnedFields[pinnedFields.length - 1];
    if (lastField) {
      sx[`& [data-field="${lastField}"]`] = {
        ...sx[`& [data-field="${lastField}"]`],
        borderRight: '2px solid transparent'
      };
      sx[
        `& .MuiDataGrid-columnHeader[data-field="${lastField}"] .MuiDataGrid-columnSeparator:hover`
      ] = {
        borderRight: `2px solid ${pallet.divider}`
      };
    }
    return sx;
  }, [pinnedFields]);
  const hasUnsavedChanges = Object.keys(assignmentChanges).length > 0;
  const apiRef = useGridApiRef();
  const [autoFitEnabled, setAutoFitEnabled] = useState(false);

  useEffect(() => {
    if (!hasSearched) return undefined;

    const updateGridHeight = () => {
      if (!gridWrapperRef.current) return;
      const { top } = gridWrapperRef.current.getBoundingClientRect();
      const reservedBottomSpace = 48 + (hasUnsavedChanges ? 64 : 0);
      setGridHeight(
        Math.max(240, window.innerHeight - top - reservedBottomSpace)
      );
    };

    updateGridHeight();
    window.addEventListener('resize', updateGridHeight);
    return () => window.removeEventListener('resize', updateGridHeight);
  }, [hasSearched, hasUnsavedChanges, loading]);

  const showToast = (message: string, severity: SnackbarSeverityType) =>
    setToast({ open: true, message, severity });

  const originalStudentsById = Object.fromEntries(
    originalStudents.map((student) => [student.studentId, student])
  );

  const allElectives = getAllElectives(students);

  const unsavedChangesMap = useMemo(() => {
    const map: Record<string, Record<string, boolean>> = {};
    Object.entries(assignmentChanges).forEach(([studentId, subjects]) => {
      map[studentId] = {};
      Object.entries(subjects).forEach(([subjectKey, meta]) => {
        const subjectIdOrKey = meta.subjectId || subjectKey;
        const changed = meta.current !== meta.original;
        map[studentId][subjectIdOrKey] = changed;
        map[studentId][subjectKey] = changed;
      });
    });
    return map;
  }, [assignmentChanges]);

  const rows = students.map((student: any, id) => ({
    id: student.studentId || id,
    studentId: student.studentId,
    studentName: student.studentName,
    enrollmentNo: student.enrollmentNo,
    rollNumber: student.rollNumber || student.rollNo,
    ...Object.fromEntries(
      allElectives.map((key) => [key, isSubjectAssigned(student, key)])
    )
  }));

  const toggleAssignment = useCallback(
    (studentId: string, subjectKey: string) => {
      if (saving) return;
      setStudents((prev) =>
        prev.map((student) => {
          if (student.studentId !== studentId) return student;

          const newElectiveAssigned = !isSubjectAssigned(student, subjectKey);
          const updated = updateStudentRecord(
            student,
            subjectKey,
            newElectiveAssigned
          );
          setAssignmentChanges((prevChanges: UpdateAssignmentRecord) =>
            updateAssignment(
              prevChanges,
              studentId,
              subjectKey,
              newElectiveAssigned,
              originalStudentsById[studentId]
            )
          );
          return updated;
        })
      );
    },
    [saving, originalStudentsById]
  );

  const columns = useMemo(
    () =>
      buildElectiveColumns(
        allElectives,
        toggleAssignment,
        (row, key) => !!row[key],
        saving,
        unsavedChangesMap,
        fixedColumnWidths
      ),
    [
      allElectives,
      toggleAssignment,
      saving,
      unsavedChangesMap,
      fixedColumnWidths
    ]
  );

  const handleAutosizeColumns = () => {
    if (autoFitEnabled) {
      // Turning off: drop the captured widths so columns go back to flex.
      setAutoFitColumnWidths({});
    }
    setAutoFitEnabled((prev) => !prev);
  };

  useEffect(() => {
    if (!autoFitEnabled) return undefined;

    const autosizableFields = columns
      .map((column) => column.field)
      .filter((field) => !pinnedFields.includes(field));

    // Wait out the sidebar's own margin transition (300ms, see
    // DashboardContainer) so this measures the settled grid width
    // instead of a mid-transition size. Also re-runs whenever the
    // pinned-column set changes, since that rebuilds the columns array
    // (with its original flex sizing) and the grid resets to it.
    const timeoutId = setTimeout(() => {
      apiRef.current
        ?.autosizeColumns({
          includeHeaders: true,
          expand: true,
          columns: autosizableFields
        })
        .then(() => {
          // Persist the fitted widths as fixed column widths so they
          // survive later layout changes (e.g. the sidebar opening or
          // closing), instead of only living in the grid's internal
          // state, which gets reset to our (flex-based) column defs on
          // the next resize.
          const widths: Record<string, number> = {};
          autosizableFields.forEach((field) => {
            const width = apiRef.current?.getColumn(field)?.computedWidth;
            if (width) widths[field] = width;
          });
          setAutoFitColumnWidths(widths);
        });
    }, 350);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFitEnabled, pinSecondColumn]);

  const executeSave = async () => {
    if (!school) return;
    const studentsMapping = buildStudentsMapping(
      assignmentChanges,
      students,
      originalStudents
    );
    if (!studentsMapping.length) return;

    try {
      setSaving(true);
      let effectiveTermIds: string[] = termIds;
      if (academicYear && savedGrade && savedDivision) {
        effectiveTermIds = await fetchTermIdsForClass(
          school,
          academicYear,
          savedGrade,
          savedDivision,
          showToast
        );
      }

      const studentsMappingBatches = chunk(
        studentsMapping,
        STUDENTS_MAPPING_BATCH_SIZE
      );

      for (const batch of studentsMappingBatches) {
        await electivesAssignmentApi.updateStudentElectiveSubjectsMapping(
          school,
          academicYear,
          { termIds: effectiveTermIds, studentsMapping: batch }
        );
      }

      setOriginalStudents(students.map((student) => ({ ...student })));
      setAssignmentChanges({});
      showToast('Elective assignments updated.', SnackbarSeverityType.success);
    } catch (error) {
      showToast('Failed to update assignments.', SnackbarSeverityType.error);
      throw error;
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleSave = () => {
    if (!Object.keys(assignmentChanges).length) return;
    setConfirmOpen(true);
  };

  const handleResults = (
    fetched: ElectiveSubjectsMapping[],
    meta: {
      academicYear: string;
      grade: string;
      division: string;
      termIds?: string[];
    }
  ) => {
    setAcademicYear(meta.academicYear);
    setStudents(fetched);
    setOriginalStudents(fetched.map((student) => ({ ...student })));
    setAssignmentChanges({});
    setHasSearched(true);
    setTermIds(meta.termIds || []);
    setSavedGrade(meta.grade);
    setSavedDivision(meta.division);
  };

  const handleFiltersChange = () => {
    setHasSearched(false);
    setAssignmentChanges({});
  };

  return (
    <>
      <Title>Students Elective Subjects</Title>
      <PageWrapper>
        <SearchFilters
          school={school}
          user={user}
          onResults={handleResults}
          showToast={showToast}
          onLoadingChange={setLoading}
          onFiltersChange={handleFiltersChange}
        />
      </PageWrapper>
      <Loader show={loading && students.length === 0} />
      {hasSearched && (
        <PageWrapper>
          <Box ref={gridWrapperRef}>
            <StyledDataGrid
              apiRef={apiRef}
              rows={rows}
              columns={columns}
              disableRowSelectionOnClick
              disableVirtualization
              loading={loading}
              sx={{ height: gridHeight, ...pinnedColumnsSx }}
              slots={{ footer: CustomFooter }}
              slotProps={{
                footer: {
                  onAutosize: handleAutosizeColumns,
                  autoFitEnabled,
                  pinSecondColumn,
                  onTogglePinSecondColumn: () =>
                    setPinSecondColumn((prev) => !prev)
                }
              }}
            />
          </Box>
          {hasUnsavedChanges && (
            <Box mt={2} display='flex' justifyContent='flex-end'>
              <Button
                variant='contained'
                onClick={handleSave}
                disabled={saving}
              >
                Update
              </Button>
            </Box>
          )}
        </PageWrapper>
      )}
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={executeSave}
        message={CONFIRM_MESSAGE}
        disable={saving}
      />
      <ToastMessage
        {...toast}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </>
  );
};

export default StudentsElectiveSubjectsPage;
